import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { Users, Zap, ShieldCheck } from 'lucide-react';

export const MeetingAttendanceCard = () => {
  const [data, setData] = useState(null);
  const [guests, setGuests] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.getMeetingAttendance()
      .then(r => { setData(r.data); setGuests(r.data.guests || ''); })
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const present = new Set(data.present_ids || []);
  const approverIds = new Set((data.approvers || []).map(a => a.id));
  const missingApprovers = (data.approvers || []).filter(a => !present.has(a.id));

  const toggle = async (id) => {
    const next = present.has(id)
      ? data.present_ids.filter(x => x !== id)
      : [...data.present_ids, id];
    setSaving(true);
    try {
      const res = await api.setMeetingAttendance(next, guests);
      setData(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save attendance'); }
    setSaving(false);
  };

  const saveGuests = async () => {
    if ((data.guests || '') === guests.trim()) return;
    try {
      const res = await api.setMeetingAttendance(data.present_ids, guests.trim());
      setData(res.data);
    } catch { toast.error('Failed to save guests'); }
  };

  return (
    <div className="bg-card border border-border rounded-sm" data-testid="meeting-attendance-card">
      <div className="px-4 py-3 border-b border-border flex flex-col sm:flex-row sm:items-center gap-2 justify-between">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-blue-400" />
          <p className="font-heading text-base text-foreground">Who is present?</p>
          <span className="text-[10px] text-muted-foreground">— set at the start of each meeting, resets on Finalize</span>
        </div>
        {data.approvers_present ? (
          <span data-testid="attendance-bypass-active"
            className="inline-flex items-center gap-1.5 text-[11px] font-medium text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-sm px-2.5 py-1">
            <Zap className="w-3.5 h-3.5" /> Decisions are final immediately — {(data.approvers || []).map(a => a.name.split(' ')[0]).join(' & ')} present
          </span>
        ) : (
          <span data-testid="attendance-bypass-inactive"
            className="inline-flex items-center gap-1.5 text-[11px] text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-sm px-2.5 py-1">
            <ShieldCheck className="w-3.5 h-3.5" />
            Standard approval flow{missingApprovers.length > 0 ? ` — ${missingApprovers.map(a => a.name.split(' ')[0]).join(' & ')} not marked present` : ''}
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-4 flex-wrap">
          {(data.members || []).map(m => (
            <label key={m.id} className="flex items-center gap-2 cursor-pointer" data-testid={`attendance-member-${m.id}`}>
              <Checkbox checked={present.has(m.id)} disabled={saving}
                onCheckedChange={() => toggle(m.id)} data-testid={`attendance-checkbox-${m.id}`} />
              <span className="text-xs text-foreground">
                {m.name}
                {approverIds.has(m.id) && <span className="ml-1 text-[9px] font-mono uppercase text-blue-400/70">approver</span>}
              </span>
            </label>
          ))}
        </div>
        <div className="flex items-center gap-2 sm:ml-auto sm:w-64">
          <Input value={guests} onChange={e => setGuests(e.target.value)} onBlur={saveGuests}
            onKeyDown={e => e.key === 'Enter' && saveGuests()}
            placeholder="Guests (optional), e.g. Luis, advisor..."
            className="bg-secondary border-border rounded-sm h-8 text-xs" data-testid="attendance-guests-input" />
        </div>
      </div>
    </div>
  );
};
